import { Logo } from '@/components/logo';
import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import type { ChallengeResponse } from '@/api/auth';

export function LoginRoute() {
  const navigate = useNavigate();
  const { login, completeChallenge, signup, isLoading, error, clearError } = useAuthStore();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [handle, setHandle] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [challenge, setChallenge] = useState<ChallengeResponse | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [searchParams] = useSearchParams();
  const expired = searchParams.get('expired') === '1';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      if (mode === 'login') {
        const res = await login(handle, password, keepSignedIn);
        if (res) { setChallenge(res); setAnswers({}); return; } // needs security questions
      } else {
        await signup(handle, password, displayName || undefined, email || undefined);
      }
      navigate('/inbox');
    } catch {
      // error set in store
    }
  };

  const handleChallenge = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    if (!challenge) return;
    try {
      await completeChallenge(challenge.challengeId, answers);
      navigate('/inbox');
    } catch (err: any) {
      // If the challenge expired / too many tries, drop back to the password form.
      if (err?.data?.expired) { setChallenge(null); setAnswers({}); }
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-white px-4 bg-cover bg-center"
      style={{ backgroundImage: 'url(/bee-swarm.avif)' }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl border-2 border-black">
        {/* Logo */}
        <div className="mb-6 flex flex-col items-center gap-3">
          <Logo className="h-24 w-auto" variant="light" />
          <p className="text-base font-medium text-black">Your email, cleaner.</p>
        </div>

        {/* Info banner for signup */}
        {mode === 'signup' && (
          <div className="mb-4 rounded-lg bg-yellow-50 border-2 border-yellow-400 px-4 py-3 text-sm text-black">
            <strong>What is B-Mail?</strong> B-Mail is a mail <em>client</em> — like Outlook or Apple Mail.
            You're creating a B-Mail account to read your existing email, not a new email address.
            Your handle is your B-Mail login. You'll connect your actual email (Gmail, etc.) after signing up.
          </div>
        )}

        {expired && (
          <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-black border-2 border-blue-400">
            Your session expired — please sign in again.
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600 border border-red-200">
            {error}
          </div>
        )}

        {challenge ? (
          <form onSubmit={handleChallenge} className="space-y-4">
            <p className="text-sm text-black">
              New sign-in location detected. Answer at least <strong>2 of 3</strong> security questions to continue.
            </p>
            {challenge.questions.map((q) => (
              <div key={q.position}>
                <label className="mb-1 block text-sm font-semibold text-black">{q.question}</label>
                <input
                  type="text"
                  autoComplete="off"
                  value={answers[String(q.position)] || ''}
                  onChange={(e) => setAnswers((a) => ({ ...a, [String(q.position)]: e.target.value }))}
                  className="w-full rounded-lg border-2 border-black bg-white px-4 py-2.5 text-black placeholder-gray-400 focus:border-bmail-gold focus:outline-none"
                  placeholder="Your answer"
                />
              </div>
            ))}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-bmail-goldfill px-4 py-3 font-bold text-black hover:bg-bmail-goldfill-hover disabled:opacity-50 transition-colors border-2 border-black"
            >
              {isLoading ? 'Verifying…' : 'Verify & sign in'}
            </button>
            <button
              type="button"
              onClick={() => { setChallenge(null); setAnswers({}); clearError(); }}
              className="w-full text-sm text-gray-600 hover:text-black"
            >
              Back to sign in
            </button>
          </form>
        ) : (
        <>
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <>
              <div>
                <label className="mb-1 block text-sm font-semibold text-black">Handle (your B-Mail login)</label>
                <input
                  type="text"
                  required
                  minLength={3}
                  maxLength={32}
                  pattern="[a-z0-9][a-z0-9-]{1,30}[a-z0-9]"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value.toLowerCase())}
                  className="w-full rounded-lg border-2 border-black bg-white px-4 py-2.5 text-black placeholder-gray-400 focus:border-bmail-gold focus:outline-none"
                  placeholder="brian"
                />
                <p className="mt-1 text-xs text-gray-500">3-32 chars, lowercase letters, numbers, hyphens</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-black">Display name (optional)</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full rounded-lg border-2 border-black bg-white px-4 py-2.5 text-black placeholder-gray-400 focus:border-bmail-gold focus:outline-none"
                  placeholder="Brian Nunes"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-black">Recovery email (optional)</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border-2 border-black bg-white px-4 py-2.5 text-black placeholder-gray-400 focus:border-bmail-gold focus:outline-none"
                  placeholder="brian@briannunes.com"
                />
                <p className="mt-1 text-xs text-gray-500">Used only for password resets — not your B-Mail login</p>
              </div>
            </>
          )}

          {mode === 'login' && (
            <div>
              <label className="mb-1 block text-sm font-semibold text-black">Handle</label>
              <input
                type="text"
                required
                value={handle}
                onChange={(e) => setHandle(e.target.value.toLowerCase())}
                className="w-full rounded-lg border-2 border-black bg-white px-4 py-2.5 text-black placeholder-gray-400 focus:border-bmail-gold focus:outline-none"
                placeholder="brian"
              />
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-semibold text-black">Password</label>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border-2 border-black bg-white px-4 py-2.5 text-black placeholder-gray-400 focus:border-bmail-gold focus:outline-none"
              placeholder="••••••••"
            />
          </div>

          {mode === 'login' && (
            <label className="flex items-center gap-2 text-sm text-black cursor-pointer select-none">
              <input
                type="checkbox"
                checked={keepSignedIn}
                onChange={(e) => setKeepSignedIn(e.target.checked)}
                className="h-4 w-4 rounded border-2 border-black accent-bmail-gold"
              />
              Keep me signed in for background sync
            </label>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg bg-bmail-goldfill px-4 py-3 font-bold text-black hover:bg-bmail-goldfill-hover disabled:opacity-50 transition-colors border-2 border-black"
          >
            {isLoading ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create B-Mail account'}
          </button>
        </form>

        {mode === 'login' && (
          <div className="mt-4 text-center">
            <Link
              to="/forgot"
              className="text-sm text-gray-600 hover:text-black transition-colors"
            >
              Forgot password?
            </Link>
          </div>
        )}

        <div className="mt-6 text-center text-sm text-gray-600">
          {mode === 'login' ? "Don't have a B-Mail account?" : 'Already have an account?'}{' '}
          <button
            type="button"
            onClick={() => {
              setMode(mode === 'login' ? 'signup' : 'login');
              clearError();
            }}
            className="font-bold text-black underline"
          >
            {mode === 'login' ? 'Create account' : 'Sign in'}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}